import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { LogOut, User, Crown, Shield, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useCombatRole, useWebSocket } from '../../../contexts/WebSocketContext';
import SessionControls from './SessionControls';
import { getAssetUsageSnapshot } from '../../../services/EntitlementService';
import { formatBytes, getTierConfig, isPremiumTier } from '../../../config/entitlements';
import { resolveRuntimeTier } from '../../../utils/runtimeTier';
import useBattleRoomCompactViewport from '../../../ui/layout/useBattleRoomCompactViewport';
import { fetchDefaultBuiltinRulesetSelection } from '../../../rules/rulesetSelections';
import './UserInfoTopbar.css';

/**
 * UserInfoTopbar - Shows current user info, session participants, and sign out option in BattleRoom
 * @date_of_change: 2025-10-01 - Created to fix GM role persistence and show user identity
 * @date_of_change: 2025-10-03 - Added participant list and collapse functionality
 */
const UserInfoTopbar = ({ onSaveSession, onLoadSession, onOverwriteSession, onGoLive }) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { isGM } = useCombatRole();
  const { sessionParticipants = [], currentSessionId, joinSession } = useWebSocket();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isCompactViewport = useBattleRoomCompactViewport();
  const [assetUsage, setAssetUsage] = useState({ totalCount: 0, totalBytes: 0 });
  const tier = resolveRuntimeTier();
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('[UserInfoTopbar] Sign out failed:', error);
    }
  };

  // date_of_change: 2026-04-17 - Promote a local session to a live shareable Battle Room.
  // Only available to the GM when the current URL has no session ID (i.e. /battleroom, not /battle/:id).
  const handleGoLive = useCallback(async () => {
    if (typeof onGoLive === 'function') {
      await onGoLive();
      return;
    }

    try {
      const { apiFetch } = await import('../../../utils/apiFetch.js');
      const rulesetSelection = await fetchDefaultBuiltinRulesetSelection(apiFetch);
      const response = await apiFetch('/api/combat', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Battle Room',
          rulesetSelection
        })
      });
      if (!response.ok) throw new Error(`Create failed: ${response.status}`);
      const sessionData = await response.json();
      const newSessionId = sessionData._id;
      await apiFetch(`/api/combat/${newSessionId}/join`, { method: 'POST' });
      await joinSession(newSessionId);
      navigate(`/battle/${newSessionId}`, { replace: true });
    } catch (error) {
      console.error('[UserInfoTopbar] Go Live failed:', error);
      alert('Failed to open a live battle room. Please try again.');
    }
  }, [joinSession, navigate, onGoLive]);

  const refreshAssetUsage = useCallback(async () => {
    const usage = await getAssetUsageSnapshot();
    setAssetUsage(usage);
  }, []);

  useEffect(() => {
    refreshAssetUsage();

    const handleAssetUsageUpdated = (event) => {
      const nextUsage = event?.detail;
      if (nextUsage && typeof nextUsage === 'object') {
        setAssetUsage(nextUsage);
        return;
      }
      refreshAssetUsage();
    };

    window.addEventListener('vcs-asset-usage-updated', handleAssetUsageUpdated);
    window.addEventListener('focus', refreshAssetUsage);

    return () => {
      window.removeEventListener('vcs-asset-usage-updated', handleAssetUsageUpdated);
      window.removeEventListener('focus', refreshAssetUsage);
    };
  }, [refreshAssetUsage]);

  // date_of_change: 2026-07-25
  // Phone landscape is an active-play surface. Start with one compact status
  // row instead of allowing session metadata to displace the battle map.
  useEffect(() => {
    if (isCompactViewport) {
      setIsCollapsed(true);
    }
  }, [isCompactViewport]);

  const vaultLabel = isPremium
    ? `Vault ${formatBytes(assetUsage.totalBytes)} / ${formatBytes(tierConfig.limits.assetStorageBytes)}`
    : `Vault ${assetUsage.totalCount}/${tierConfig.limits.assetCount}`;

  const renderVaultBadge = () => (
    <span className="text-xs text-gray-600 font-medium bg-gray-100 px-2 py-1 rounded">
      {vaultLabel}
    </span>
  );

  if (!user) {
    return null; // Don't render if user is not authenticated
  }

  const getRoleDisplay = () => {
    if (isGM) {
      return (
        <span className="flex items-center gap-1 text-amber-600 font-semibold">
          <Crown size={16} />
          Game Master
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-blue-600">
        <Shield size={16} />
        Player
      </span>
    );
  };

  // Collapsed view - minimal bar
  if (isCollapsed) {
    return (
      <div className="battle-user-topbar battle-user-topbar--collapsed flex items-center justify-between">
        <div className="battle-user-topbar__compact-status flex items-center gap-2 text-sm">
          {getRoleDisplay()}
          {renderVaultBadge()}
          {sessionParticipants.length > 0 && (
            <span className="text-gray-500">
              ({sessionParticipants.length} {sessionParticipants.length === 1 ? 'participant' : 'participants'})
            </span>
          )}
        </div>
        <button
          onClick={() => setIsCollapsed(false)}
          className="battle-user-topbar__icon-button"
          title="Expand session info"
        >
          <ChevronDown size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="battle-user-topbar battle-user-topbar--expanded flex items-center justify-between">
      <div className="battle-user-topbar__details flex items-center gap-4">
        <div className="flex items-center gap-2">
          <User size={20} className="text-gray-600" />
          <span className="font-medium text-gray-900">
            {user.firstName || user.username || user.emailAddresses?.[0]?.emailAddress || 'Unknown User'}
          </span>
        </div>
        {getRoleDisplay()}

        {/* Teleportation Circle */}
        {currentSessionId && (
          <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
            Teleportation Circle: {currentSessionId.slice(-8)}
          </span>
        )}

        {renderVaultBadge()}

        {/* Participants */}
        {sessionParticipants.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users size={16} />
            <span>
              {sessionParticipants.length} {sessionParticipants.length === 1 ? 'Participant' : 'Participants'}
            </span>
            <div className="flex items-center gap-1 ml-2">
              {sessionParticipants.map((participant, index) => {
                const isGMParticipant = participant.role === 'gm' || participant.isGameMaster;
                const roleLabel = isGMParticipant ? 'Game Master' : 'Player';
                const name = participant.alias || participant.userName || null;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md"
                    title={name ? `${roleLabel} — ${name}` : roleLabel}
                  >
                    {isGMParticipant ? (
                      <Crown size={14} className="text-amber-600" />
                    ) : (
                      <Shield size={14} className="text-blue-600" />
                    )}
                    <span className="text-xs">{name ? `${roleLabel} — ${name}` : roleLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="battle-user-topbar__actions flex items-center gap-3">
        {/* Session Options moved here from Toolbar (GM primary use, but okay to show to all) */}
        <SessionControls
          onSave={onSaveSession || (() => {})}
          onLoad={onLoadSession || (() => {})}
          onOverwrite={onOverwriteSession || (() => {})}
          onGoLive={handleGoLive}
          isGM={isGM}
          className="topbar-session-controls"
        />
        <button
          onClick={() => setIsCollapsed(true)}
          className="battle-user-topbar__icon-button"
          title="Minimize session info"
        >
          <ChevronUp size={16} />
        </button>
        <button
          onClick={handleSignOut}
          className="battle-user-topbar__sign-out flex items-center gap-2"
          title="Sign Out"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default UserInfoTopbar;
